import type { Localized } from "./types";

/** Resolve a localized value for a locale, falling back to any other locale. */
export function lv<T>(value: Localized<T> | null | undefined, locale: string, fallback?: NoInfer<T>): T | undefined;
export function lv<T>(value: Localized<T> | T | null | undefined, locale: string, fallback?: T): T | undefined;
export function lv<T>(value: Localized<T> | T | null | undefined, locale: string, fallback?: T): T | undefined {
  if (value == null) return fallback;
  if (typeof value !== "object" || Array.isArray(value)) return value as T;
  const rec = value as Localized<T>;
  if (rec[locale] != null) return rec[locale];
  for (const k of Object.keys(rec)) if (rec[k] != null) return rec[k];
  return fallback;
}

export function setLv<T>(value: Localized<T> | undefined, locale: string, v: T): Localized<T> {
  return { ...(value && typeof value === "object" ? value : {}), [locale]: v };
}

/**
 * Content locale for a public request: a configured locale domain wins, then a
 * `/<locale>` path prefix, otherwise the site default. Shared by the page
 * renderer and the proxy (which stamps the result onto `<html lang>`).
 */
export function resolveLocale(
  settings: { defaultLocale: string; locales: string[]; localeDomains?: Record<string, string> },
  host: string | null | undefined,
  firstSegment: string | null | undefined,
): string {
  const h = (host || "").split(":")[0].toLowerCase();
  for (const [loc, domain] of Object.entries(settings.localeDomains || {})) {
    if (h && h === String(domain).toLowerCase() && settings.locales.includes(loc)) return loc;
  }
  if (firstSegment && settings.locales.includes(firstSegment) && firstSegment !== settings.defaultLocale) {
    return firstSegment;
  }
  return settings.defaultLocale;
}

/** Build a locale-prefixed path; the default locale (first in list) has no prefix. */
export function localeHref(locale: string, locales: string[], path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (!locales.length || locale === locales[0]) return clean;
  return `/${locale}${clean === "/" ? "" : clean}`;
}

/**
 * Absolute or path-based URL for a locale: when a domain is configured for
 * the target locale, link cross-domain; otherwise use the /<locale> prefix.
 */
export function localeUrl(
  locale: string,
  locales: string[],
  path: string,
  domains?: Record<string, string>
): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (domains?.[locale]) {
    const proto = typeof location !== "undefined" && location.protocol === "http:" ? "http" : "https";
    return `${proto}://${domains[locale]}${clean === "/" ? "" : clean}`;
  }
  return localeHref(locale, locales, clean);
}

export const LOCALE_NAMES: Record<string, string> = {
  nl: "Nederlands",
  en: "English",
  de: "Deutsch",
  fr: "Français",
};

/** Switch the resource translation while preserving the current query and fragment. */
export function localeSwitchUrl(target: string, current: string, locales: string[],
  currentLocation: { pathname: string; search: string; hash: string },
  domains?: Record<string, string>, alternates?: Record<string, string>): string | null {
  if (!locales.includes(target) || (alternates && !alternates[target])) return null;
  let path = currentLocation.pathname;
  if (current !== locales[0] && (path === `/${current}` || path.startsWith(`/${current}/`))) {
    path = path.slice(current.length + 1) || "/";
  }
  return localeUrl(target, locales, alternates?.[target] || path, domains) + currentLocation.search + currentLocation.hash;
}
